import type { DeviceDto } from './device.model';

export interface PartId {
  partType?: PartId.PartTypeEnum;
  /**
   * Raw sub type as reported by CBDA, e.g. 'HW_SUB_TYPE_WING_R24_VECTOR'. Formatted for
   * display with formatPartSubType(); PartSubTypeEnum below mirrors the mapped values that
   * other backend endpoints return.
   */
  partSubType?: string;
  /**
   * Id of the part. This is unique among parts of the same type and subtype.
   */
  id?: string;
}
export namespace PartId {
  export type PartTypeEnum =
    | 'body'
    | 'left_wing'
    | 'right_wing'
    | 'rear'
    | 'payload'
    | 'battery'
    | 'other';
  export const PartTypeEnum = {
    Body: 'body' as PartTypeEnum,
    LeftWing: 'left_wing' as PartTypeEnum,
    RightWing: 'right_wing' as PartTypeEnum,
    Rear: 'rear' as PartTypeEnum,
    Payload: 'payload' as PartTypeEnum,
    Battery: 'battery' as PartTypeEnum,
    Other: 'other' as PartTypeEnum,
  };
  export type PartSubTypeEnum =
    | 'unknown'
    | 'vectorR24'
    | 'scorpionR24'
    | 'vectorR25'
    | 'scorpionR25'
    | 'trinity'
    | 'trinity9000'
    | 'aiPayload'
    | 'sbp120'
    | 'sbp180';
  export const PartSubTypeEnum = {
    Unknown: 'unknown' as PartSubTypeEnum,
    VectorR24: 'vectorR24' as PartSubTypeEnum,
    ScorpionR24: 'scorpionR24' as PartSubTypeEnum,
    VectorR25: 'vectorR25' as PartSubTypeEnum,
    ScorpionR25: 'scorpionR25' as PartSubTypeEnum,
    Trinity: 'trinity' as PartSubTypeEnum,
    Trinity9000: 'trinity9000' as PartSubTypeEnum,
    AiPayload: 'aiPayload' as PartSubTypeEnum,
    Sbp120: 'sbp120' as PartSubTypeEnum,
    Sbp180: 'sbp180' as PartSubTypeEnum,
  };
}

export interface Part {
  type?: string;
  partId?: PartId;
}

export interface Uav {
  hardwareRevision?: number;
  unitId?: number;
}
export interface PartWithCounterDetailsAllOfCounters {
  /**
   * copy of CBDA_generated.yaml `components.schemas.MetricDto.properties.type`
   */
  type?: string;
  value?: string;
  threshold?: string;
  unit?: string;
  /**
   * copy of CBDA_generated.yaml `components.schemas.MetricDto.properties.valueType`
   */
  valueType?: PartWithCounterDetailsAllOfCounters.ValueTypeEnum;
}
export interface PartWithCounterDetailsAllOfCountersForTable
  extends PartWithCounterDetailsAllOfCounters {
  percentage?: number;
}

export namespace PartWithCounterDetailsAllOfCounters {
  export type ValueTypeEnum =
    | 'unknown'
    | 'seconds'
    | 'minutes'
    | 'hours'
    | 'days'
    | 'cycles';
  export const ValueTypeEnum = {
    Unknown: 'unknown' as ValueTypeEnum,
    Seconds: 'seconds' as ValueTypeEnum,
    Minutes: 'minutes' as ValueTypeEnum,
    Hours: 'hours' as ValueTypeEnum,
    Days: 'days' as ValueTypeEnum,
    Cycles: 'cycles' as ValueTypeEnum,
  };
}

export interface PartWithCounterDetails extends Part {
  uav?: Uav;
  counters?: Array<PartWithCounterDetailsAllOfCounters>;
  /**
   * Raw part type as reported by CBDA, taken from QsUavPart details. Kept so that parts
   * the backend could not map to a known type ('other') can still be labelled.
   */
  qsUavPartType?: string;
}

export interface PartWithCounterDetailsForTable extends PartWithCounterDetails {
  counterToDisplay: PartWithCounterDetailsAllOfCountersForTable;
  counters?: Array<PartWithCounterDetailsAllOfCountersForTable>;
  lastConnectedDevice?: DeviceDto;
}

export interface PartWithMaintenanceDetails extends Part {
  id?: string;
  typeOfMaintenance?: string;
  createdAt?: string;
  description?: string;
  maintainer?: string;
  detailedDescription?: string;
  /**
   * Additional specification of the user e.g. Azure Entra User ID
   */
  detailedMaintainer?: string;
  attachments?: Array<string>;
}

export type PartCounterCsvRow = {
  partType: string;
  partSubType: string;
  partId: string;
  lastConnectedDevice: string;
  counterType: string;
  counterValue: string;
  threshold: string;
  percentage: string;
};
